'use client';
import {
  SplitFactory,
  SplitFactoryProvider,
  useSplitTreatments,
} from '@splitsoftware/splitio-react';
import {v4 as uuidv4} from 'uuid';
import {isRequestedFromGoogleProxy} from '../utils/domain';

import type React from 'react';
import {useCookies} from 'react-cookie';
import {FlagValue} from './FeatureFlagUtils';
import {getMetroCodeByMarketCode} from './MarketMetroMap';
interface SplitFlagProviderProps {
  children: React.ReactElement | React.ReactNode;
}

interface UserAttributesType {
  address?: string;
  market?: string;
}

const getUserIdentifier = () => {
  const cookieName = 'splitUniqueIdentifier';
  const [cookies, setCookie] = useCookies([cookieName]);
  const userIdentifier = cookies?.[cookieName];
  if (!userIdentifier) {
    const userIdentifier = uuidv4();
    setCookie(cookieName, userIdentifier, {
      domain: 'gfiber.com',
      secure: true,
      sameSite: 'strict',
      httpOnly: false,
    });
  }
  return userIdentifier;
};

export const SplitFlagProvider = ({
  children,
}: SplitFlagProviderProps): React.JSX.Element => {
  if (isRequestedFromGoogleProxy()) {
    return <>{children}</>;
  }
  const sdkConfig: SplitIO.IBrowserSDK = SplitFactory({
    core: {
      authorizationKey: process.env.NEXT_PUBLIC_SPLIT_CLIENT_ID || 'localhost',
      // key represents your internal user id, or the account id that
      // the user belongs to.
      // This could also be a cookie you generate for anonymous users.
      key: getUserIdentifier(),
    },
  });
  return (
    <SplitFactoryProvider factory={sdkConfig}>{children}</SplitFactoryProvider>
  );
};

const getTreatments = (
  names: string[],
  userAttributes: UserAttributesType,
  fallbackValue = FlagValue.OFF,
) => {
  const {treatments: initialTreatments, isReady} = useSplitTreatments({
    names,
    attributes: {
      address: userAttributes?.address || '',
      metro: getMetroCodeByMarketCode(userAttributes?.market) || '',
    },
  });
  const treatments: {[key: string]: string} = {};

  for (const feature in initialTreatments) {
    const {treatment: defaultTreatmentValue} = initialTreatments[`${feature}`];

    // set fallbackValue if treatment is control
    if (defaultTreatmentValue === 'control') {
      treatments[`${feature}`] = fallbackValue;
    } else {
      treatments[`${feature}`] = defaultTreatmentValue;
    }
  }
  return {treatments, isReady};
};

export const useTreatment = (
  name: string,
  userAttributes: UserAttributesType,
  fallbackValue = FlagValue.OFF,
) => {
  if (isRequestedFromGoogleProxy()) {
    return {treatment: null, isReady: true};
  }
  const {treatments, isReady} = getTreatments(
    [name],
    userAttributes,
    fallbackValue,
  );
  return {treatment: treatments[`${name}`], isReady};
};

export const useTreatments = (
  names: string[],
  userAttributes: UserAttributesType,
  fallbackValue = FlagValue.OFF,
) => {
  if (isRequestedFromGoogleProxy()) {
    return {treatments: null, isReady: true};
  }
  const {treatments, isReady} = getTreatments(
    names,
    userAttributes,
    fallbackValue,
  );

  return {treatments, isReady};
};
